/**
 * §8.3 Documentation Center — interactive doc browser.
 * Left rail: 9 doc sections (click to switch).
 * Right pane: matching preview article with breadcrumb, title, body
 * and code snippet.
 */

import React, { useState } from 'react'
import clsx from 'clsx'
import {
  Rocket, ServerCog, Bot, LineChart, Building2, CreditCard, Layers, Cog,
  ShieldCheck, Search, ListTree, BookMarked, Copy, Printer, ChevronRight, Sparkles, ArrowRight,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading, GhostButton } from '@/components/landing/design-system'
import { DOC_SECTIONS, DOC_FEATURES } from '../data'
import { TONE } from '../_shared'

const NAV_ICONS = { Rocket, ServerCog, Bot, LineChart, Building2, CreditCard, Layers, Cog, ShieldCheck }
const FEAT_ICONS = { Search, ListTree, BookMarked, Copy, Printer }

export default function Documentation() {
  const [active, setActive] = useState(DOC_SECTIONS[0].id)
  const current = DOC_SECTIONS.find((d) => d.id === active) || DOC_SECTIONS[0]
  const tone = TONE[current.tone] || TONE.orange
  const CurrentIcon = NAV_ICONS[current.iconKey] || Sparkles

  return (
    <section id="documentation" className="relative py-24 md:py-28 scroll-mt-24">
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Documentation Center</Eyebrow>
          <SectionHeading align="center"
            title={<>The full Kalki Ai Cloud reference,{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">organised by module.</span></>}
            subtitle="Nine top-level sections, hundreds of articles. Predictive search, table of contents and reading progress are built-in."
          />
        </div>

        {/* Browser-style doc pane */}
        <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] p-4 md:p-5 backdrop-blur-xl overflow-hidden">
          <div className="pointer-events-none absolute inset-0 opacity-60"
            style={{ background: 'radial-gradient(60% 40% at 20% 0%, rgba(255,193,7,0.08), transparent 70%)' }} />

          {/* browser chrome */}
          <div className="relative flex items-center gap-1.5 rounded-md bg-black/40 px-2.5 py-2 border border-white/5 mb-3">
            <div className="h-2 w-2 rounded-full bg-red-500/70" />
            <div className="h-2 w-2 rounded-full bg-amber-500/70" />
            <div className="h-2 w-2 rounded-full bg-emerald-500/70" />
            <div className="ml-3 flex-1 h-5 rounded-md bg-white/[0.04] flex items-center px-2 text-[10px] font-mono text-zinc-500">
              docs.kalkiai.trade/{current.id}
            </div>
            <div className="flex items-center gap-1 text-[10px] font-mono text-emerald-400 pr-1">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> synced
            </div>
          </div>

          {/* two-col */}
          <div className="relative grid grid-cols-12 gap-3">
            {/* LEFT nav */}
            <div className="col-span-4 md:col-span-3 rounded-lg border border-white/10 bg-black/40 p-2">
              <div className="text-[9.5px] font-mono uppercase tracking-wider text-zinc-500 px-2 py-1.5">Sections</div>
              <nav className="flex flex-col gap-0.5">
                {DOC_SECTIONS.map((d) => {
                  const Icon = NAV_ICONS[d.iconKey] || Sparkles
                  const t = TONE[d.tone] || TONE.orange
                  const on = active === d.id
                  return (
                    <button key={d.id} onClick={() => setActive(d.id)}
                      className={clsx(
                        'flex items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors',
                        on
                          ? 'bg-white/[0.06] border border-white/10'
                          : 'hover:bg-white/[0.03] border border-transparent',
                      )}>
                      <Icon strokeWidth={1.8} className="shrink-0" style={{ width: 12, height: 12, color: on ? t.hex : 'rgba(255,255,255,0.5)' }} />
                      <span className={clsx('text-[12px] font-semibold truncate', on ? 'text-white' : 'text-zinc-400')}>{d.label}</span>
                      {on && <ChevronRight className="ml-auto h-3 w-3 shrink-0" style={{ color: t.hex }} />}
                    </button>
                  )
                })}
              </nav>
            </div>

            {/* RIGHT preview */}
            <div className="col-span-8 md:col-span-9 rounded-lg border border-white/10 bg-black/40 p-4 md:p-5">
              {/* breadcrumb */}
              <div className="flex items-center gap-1 text-[10.5px] font-mono uppercase tracking-wider text-zinc-500 mb-2">
                <span>Docs</span>
                <ChevronRight className="h-3 w-3" />
                <span style={{ color: tone.hex }}>{current.label}</span>
              </div>
              <div className="flex items-center gap-3 mb-2">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-md border"
                  style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                  <CurrentIcon strokeWidth={1.8} style={{ width: 14, height: 14 }} />
                </span>
                <h3 className="text-lg md:text-xl font-semibold text-white leading-tight">{current.label}</h3>
              </div>
              <p className="text-[13px] text-zinc-400 leading-relaxed">{current.tagline}</p>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
                {current.articles.map((a, i) => (
                  <div key={a} className="flex items-center gap-2 rounded-md border border-white/10 bg-white/[0.02] px-3 py-2">
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-md border border-white/10 bg-white/[0.03] text-[10px] font-mono text-zinc-400">{String(i + 1).padStart(2, '0')}</span>
                    <span className="text-[12.5px] font-medium text-zinc-200">{a}</span>
                    <ArrowRight className="ml-auto h-3 w-3 text-zinc-500" />
                  </div>
                ))}
              </div>

              {/* code snippet mock */}
              <div className="mt-4 rounded-lg border border-white/10 bg-black/60 overflow-hidden">
                <div className="flex items-center justify-between border-b border-white/5 px-3 py-1.5">
                  <span className="text-[9.5px] font-mono uppercase tracking-wider text-zinc-500">bash</span>
                  <button className="inline-flex items-center gap-1 text-[9.5px] font-mono text-zinc-400 hover:text-white">
                    <Copy className="h-3 w-3" /> copy
                  </button>
                </div>
                <pre className="px-3 py-2 text-[11.5px] font-mono text-zinc-300 leading-relaxed">
<span className="text-zinc-500"># provision a fresh {current.label.toLowerCase()} tenant</span>{'\n'}<span style={{ color: tone.hex }}>brand</span> tenant create --name acme --plan pro{'\n'}<span className="text-zinc-500"># response</span>{'\n'}{'{ "tenant_id": "acme", "status": "provisioned" }'}
                </pre>
              </div>
            </div>
          </div>

          {/* feature chips */}
          <div className="relative mt-4 flex flex-wrap gap-2">
            {DOC_FEATURES.map((f) => {
              const Icon = FEAT_ICONS[f.iconKey] || Sparkles
              return (
                <span key={f.label} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[10.5px] font-mono uppercase tracking-wider text-zinc-300">
                  <Icon className="h-3 w-3 text-brand-gold" strokeWidth={2} />
                  {f.label}
                </span>
              )
            })}
            <GhostButton href="#documentation" size="sm" trailingIcon className="ml-auto">
              Browse all docs
            </GhostButton>
          </div>
        </div>
      </Container>
    </section>
  )
}
